// "Kunden vender tilbage med ny tid" (design D – Mangler tid): kunden ringer og vil flytte, men kender ikke den nye tid.
// Kontoret frigiver tiden og steameren med det samme; bookingen får status 'mangler_tid' og et link (gyldigt 30 dage),
// hvor kunden selv vælger en ny ledig tid. Freemi påminder kunden efter X dage (og én gang til efter lige så mange dage)
// og kontoret efter Y dage. Er den oprindelige tid under flytTimer timer væk (standard 24), kan kontoret opkræve flyttegebyret
// (klientens gebyrer.flyt) – det lægges på bookingen med det samme.
// Samme regler i testversionen (src/app/api/demo.ts), edge-funktionen "booking" og databasen (20261028850000_mangler_tid.sql).
import { fejl } from './fejl.ts';
import { iDag, lokalMin, minutterTil } from './dato.ts';
import { varselGebyr, type GebyrIndst } from './gebyrer.ts';
import * as M from './motor.ts';
import type { Booking } from './typer.ts';

export const MT_GYLDIG_DAGE = 30;
/** Kunden påmindes højst så mange gange (første gang + én gang til efter samme antal dage). */
export const MT_KUNDE_GANGE = 2;
export type MtKanal = 'mail' | 'sms';

/** Én frigivet tid: påmindelser, notat, aftalt gebyr og kundens link. */
export type ManglerTid = {
  booking_id: number; token: string; oprettet: string; udloeber: string; af: string;
  oprindelig: { dato: string; start: number | null; slut: number | null; steamer_id: string | null; steamer: string | null };
  kunde_dage: number; kanaler: MtKanal[]; kunde_sendt: number; kunde_sidst: string | null;
  kontor_dage: number; kontor_til: string | null; kontor_varslet: string | null;   // kontor_til: profil (null = hele kontoret)
  notat: string;
  gebyr: number;            // aftalt flyttegebyr ekskl. moms (0 = flyttes gratis)
  aabnet: string | null;    // kunden har åbnet linket (første gang)
  valgt: string | null;     // kunden har valgt ny tid
  lukket: string | null;    // bookingen har fået tid på anden vis (kontoret) eller er aflyst
};
export type FrigivInput = { kundeDage: number; kanaler: MtKanal[]; kontorDage: number; kontorTil: string | null; notat: string; gebyr: boolean };

/** Tjek og ryd op i kontorets valg. */
export function tjekFrigiv(v: FrigivInput): FrigivInput {
  const kd = Math.round(Number(v.kundeDage)), od = Math.round(Number(v.kontorDage));
  if (!(kd >= 1 && kd <= 14)) throw fejl('Påmind kunden efter 1 til 14 dage.', { kundeDage: 'Vælg 1–14 dage.' });
  if (!(od >= 1 && od <= 30)) throw fejl('Påmind kontoret efter 1 til 30 dage.', { kontorDage: 'Vælg 1–30 dage.' });
  const kanaler = (['mail', 'sms'] as const).filter((k) => v.kanaler?.includes(k));
  const notat = String(v.notat ?? '').trim();
  if (notat.length > 500) throw fejl('Notatet må højst være 500 tegn.', { notat: 'Højst 500 tegn.' });
  return { kundeDage: kd, kanaler, kontorDage: od, kontorTil: v.kontorTil || null, notat, gebyr: !!v.gebyr };
}

/** Kan bookingen frigives? (Kun kommende bookinger med en tid.) */
export const kanFrigives = (b: Pick<Booking, 'status' | 'start'>) => ['afventer', 'godkendt', 'skal_rykkes'].includes(b.status) && b.start != null;

/** Flyttegebyret, hvis den oprindelige tid er under flytTimer timer væk (null = intet gebyr og ingen afkrydsning). Beløb ekskl. moms. */
export function frigivGebyr(b: Pick<Booking, 'dato' | 'start'>, g: GebyrIndst): { beloeb: number; timer: number } | null {
  if (b.start == null || !b.dato) return null;
  const v = varselGebyr('flyt', minutterTil(b.dato, b.start), g);
  return v ? { beloeb: v.beloeb, timer: v.timer } : null;
}

const DAG = 864e5;
const ms = (t: string | null) => (t ? Date.parse(t) : NaN);
/** Linket kan stadig bruges. */
export const mtAaben = (m: ManglerTid, nu = Date.now()) => !m.valgt && !m.lukket && nu < ms(m.udloeber);
/** Skal kunden have en påmindelse nu? */
export const kundeForfalden = (m: ManglerTid, nu = Date.now()) =>
  mtAaben(m, nu) && m.kanaler.length > 0 && m.kunde_sendt < MT_KUNDE_GANGE && nu >= ms(m.oprettet) + m.kunde_dage * (m.kunde_sendt + 1) * DAG;
/** Skal kontoret have en påmindelse nu (én gang)? */
export const kontorForfalden = (m: ManglerTid, nu = Date.now()) => !m.valgt && !m.lukket && !m.kontor_varslet && nu >= ms(m.oprettet) + m.kontor_dage * DAG;
/** Fremhæves på I dag ("Ring i dag"), når kontorets frist er nået. */
export const skalRinges = (m: ManglerTid, nu = Date.now()) => !m.valgt && !m.lukket && nu >= ms(m.oprettet) + m.kontor_dage * DAG;
export const dageSiden = (m: ManglerTid, nu = Date.now()) => Math.max(0, Math.round((Date.parse(iDag(new Date(nu)) + 'T00:00:00Z') - Date.parse(iDag(new Date(m.oprettet)) + 'T00:00:00Z')) / DAG));

const UD = ['søn.', 'man.', 'tirs.', 'ons.', 'tors.', 'fre.', 'lør.'];
const MD = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];
/** "lør. 31. okt." (dansk tid) */
export function kortDagTid(t: number | string): string {
  const [y, m, d] = iDag(new Date(t)).split('-').map(Number);
  return `${UD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d}. ${MD[m - 1]}`;
}
const klok = (t: string) => { const m = lokalMin(new Date(t)); return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; };

/** "Sidste kontakt" på listen over kunder, der mangler en ny tid. */
export function sidsteKontakt(m: ManglerTid, spaerret = false, nu = Date.now()): string {
  if (m.valgt) return `Kunden valgte ny tid ${kortDagTid(m.valgt)}`;
  const udloebet = nu >= ms(m.udloeber) ? ' · linket er udløbet' : '';
  const aabnet = m.aabnet ? `åbnet ${kortDagTid(m.aabnet)}, ingen tid valgt` : 'ikke åbnet';
  if (spaerret) return `Udsendelse til kunder er slået fra – ring til kunden${m.aabnet ? ' · ' + aabnet : ''}${udloebet}`;
  if (!m.kanaler.length) return `Frigivet ${kortDagTid(m.oprettet)} · ${m.af} · ingen påmindelse til kunden${udloebet}`;
  if (!m.kunde_sendt) {
    const dag = iDag(new Date(m.oprettet)) === iDag(new Date(nu)) ? 'i dag' : kortDagTid(m.oprettet);
    return `Frigivet ${dag} ${klok(m.oprettet)} · ${m.af} · link sendes ${kortDagTid(ms(m.oprettet) + m.kunde_dage * DAG)}${udloebet}`;
  }
  const kanal = m.kanaler.length > 1 ? 'Link' : m.kanaler[0] === 'sms' ? 'SMS-link' : 'Mail-link';
  return `${kanal} sendt ${m.kunde_sendt > 1 ? `${m.kunde_sendt} gange` : kortDagTid(m.kunde_sidst ?? m.oprettet)} · ${aabnet}${udloebet}`;
}

/** Påmindelsen til kunden (gang 1 eller 2). Samme tekster i databasen (pixie_mangler_tid_tjek). */
/** Skabelonnavnet på kundens besked med linket (beskeder.skabelon) – samme i drift (pixie_mangler_tid_tjek). */
export const MT_SKABELON = 'Ny tid: link til kunden';
export function kundeBesked(kanal: MtKanal, gang: number, a: { fornavn: string; firma: string; telefon: string; link: string }): { emne: string; tekst: string } {
  if (kanal === 'sms') return { emne: '', tekst: gang > 1
    ? `Hej ${a.fornavn}. Husk at vælge en ny tid til din bilrens: ${a.link} – Mvh ${a.firma}`
    : `Hej ${a.fornavn}. Du ville finde en ny tid til din bilrens. Vælg den her, når det passer dig: ${a.link} – Mvh ${a.firma}` };
  return {
    emne: gang > 1 ? 'Husk at vælge ny tid til din bilrens' : 'Vælg ny tid til din bilrens',
    tekst: `Hej ${a.fornavn}\n\nDu ville finde en ny tid til din bilrens. Prisen og alt andet er det samme – du vælger bare tiden her:\n${a.link}\n\n`
      + `Linket virker i ${MT_GYLDIG_DAGE} dage.${a.telefon ? ` Vil du hellere ringe, så ring på ${a.telefon}.` : ''}\n\nVenlig hilsen\n${a.firma}`,
  };
}

// ---------- Ledige tider til kundens side (bookingens egen bil, ydelse og tilvalg – også ydelser, der ikke er online) ----------
export type NyTidBooking = Pick<Booking, 'id' | 'postnr' | 'ydelse_id' | 'tilvalg'> & { biltype_id: string };
const biler = (b: NyTidBooking): M.BilValgPris[] => [{ biltype_id: b.biltype_id, ydelse_id: b.ydelse_id, tilvalg: b.tilvalg.filter((t) => !t.ekstra_id).map((t) => ({ tilvalg_id: t.tilvalg_id, antal: t.antal })) }];
export function nyTidTider(d: M.MotorData, dato: string, b: NyTidBooking): { start: number; slut: number; steamer_id: string }[] {
  if (dato < iDag()) return [];
  try { return M.tiderFor(d, dato, b.postnr, biler(b), b.id, false); }
  catch { return M.tiderFor(d, dato, b.postnr, [{ biltype_id: b.biltype_id, ydelse_id: b.ydelse_id }], b.id, false); }
}
export function nyTidDage(d: M.MotorData, fra: string, dage: number, b: NyTidBooking): Record<string, number> {
  const ud: Record<string, number> = {};
  const t0 = Date.parse(fra + 'T00:00:00Z');
  for (let i = 0; i < Math.min(Math.max(1, dage), 31); i++) {
    const dato = new Date(t0 + i * DAG).toISOString().slice(0, 10);
    ud[dato] = nyTidTider(d, dato, b).length;
  }
  return ud;
}
